import React from 'react'
import PropTypes from 'prop-types'
import toLower from 'lodash/toLower'
import replace from 'lodash/replace'
import { connect } from 'react-redux'
import { Map } from 'immutable'
import { H2, HEADING_TYPES } from '../Heading/index'
import Button, { TYPES } from '../Button/index'
import { handleFocusNavigation, FOCUS_SELECTOR } from './utils'

export const MODAL_TYPES = {
  DEFAULT: 'DEFAULT',
  MOBILE_BOTTOM_ALIGNED: 'MOBILE_BOTTOM_ALIGNED',
  ROYAL: 'ROYAL',
  ROYAL_VIDEO: 'ROYAL_VIDEO',
}

function depropigate (e) {
  e.stopPropagation()
}

function applyBodyClass (visible) {
  const { document = {} } = global
  const { body } = document
  if (body && body.classList) {
    if (visible) body.classList.add('body__modal--open')
    else body.classList.remove('body__modal--open')
  }
}

function getClassName (className, type) {
  const cls = ['modal']
  if (className) cls.push(className)
  if (type) cls.push(`modal--${type.toLowerCase().replaceAll('_', '-')}`)
  return cls.join(' ')
}

class Modal extends React.Component {
  //
  constructor (props) {
    super(props)
    this.state = {
      error: false,
      inner: {},
      body: {},
    }
  }

  componentDidMount () {
    const { props, state, focusInitial } = this
    const { visible } = props
    const { error } = state

    focusInitial()
    applyBodyClass(!error && visible)
    setTimeout(() => this.setDynamicStyles())
    window.addEventListener('resize', this.onResize)
    // Add focus trap event listener for modals
    this.boundHandleKeyDown = this.handleKeyDown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeyDown, true)
    // if the content inside change its size apply the inline styles
    // i.e. showing an error message that increases the height.
    this.observer = new ResizeObserver((() => {
      this.setDynamicStyles()
    }))
    this.observer.observe(this.$inner)
  }

  componentDidUpdate (prevProps) {
    if (process.env.BROWSER) {
      const { children: prevChildren } = prevProps
      const { props, state } = this
      const { visible, children } = props
      const { error } = state
      applyBodyClass(!error && visible)

      if (prevChildren !== children) {
        this.setDynamicStyles()
      }
    }
  }

  // eslint doesn't recognize componentDidCatch yet
  // eslint-disable-next-line
  componentDidCatch (error) {
    if (!process.env.BROWSER) {
      throw error
    }
    applyBodyClass(false)
    this.setState(() => ({
      error: true,
    }))
  }

  componentWillUnmount () {
    const { props } = this
    const { onReturnFocus } = props
    applyBodyClass(false)
    onReturnFocus()
    window.removeEventListener('resize', this.onResize)
    // Remove focus trap event listener
    if (this.boundHandleKeyDown) {
      document.removeEventListener('keydown', this.boundHandleKeyDown, true)
    }
    this.observer.disconnect()
  }

  onResize = () => {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = null
    }
    if (this.$overlay) {
      this.resizeTimer = setTimeout(() => {
        this.setDynamicStyles()
      }, 400)
    }
  }

  handleKeyDown = (event) => {
    const { visible } = this.props
    if (!visible) return

    const modalElement = this.$overlay
    if (!modalElement) return

    handleFocusNavigation(event, modalElement)
  }

  setDynamicStyles () {
    //
    const styles = {
      inner: {},
      body: {},
    }

    if (this.$overlay && this.$overlay.clientWidth >= 480) {
      const margins = this.$body.offsetTop * 2 // for top and bottom margins
      const totalHeight = this.$overlay.clientHeight
      const marginsHelper = (window.location.pathname !== '/') ? margins : ((margins / 2) + 40)
      const innerHeight = this.$inner.clientHeight + marginsHelper

      if (innerHeight < totalHeight) {
        styles.inner = {
          height: `${innerHeight}px`,
          top: `${(totalHeight - innerHeight) / 2}px`,
        }
        styles.body = {
          overflow: 'visible',
        }
      }
    }
    this.setState(() => styles)
  }

  setCancelRef = (el) => {
    this.$cancel = el
  }

  setOverlayRef = (el) => {
    this.$overlay = el
  }

  setModalBodyRef = (el) => {
    this.$body = el
  }

  setModalBodyInnerRef = (el) => {
    this.$inner = el
  }

  focusInitial = () => {
    const { $overlay } = this
    if ($overlay) {
      const $first = $overlay.querySelector(FOCUS_SELECTOR)
      if ($first) $first.focus()
    }
  }

  update = () => {
    this.setDynamicStyles()
  }

  renderChildren () {
    const { props } = this
    const { children } = props
    return React.Children.map(children, (child) => (
      React.cloneElement(child, { update: this.update })
    ))
  }

  render () {
    const { props, state } = this
    const {
      title,
      visible,
      className,
      onDismiss,
      hideDismiss,
      hideOverflow,
      type,
      staticText,
    } = props
    const titleId = title ? toLower(replace(title, /[^a-z]/gi, '')) : ''
    const { error, inner, body } = state
    if (error || !visible) return null
    return (
      // eslint-disable-next-line
      <section
        role="dialog"
        aria-modal="true"
        onClick={hideDismiss ? null : onDismiss}
        className={getClassName(className, type)}
        ref={this.setOverlayRef}
        aria-labelledby={`modal-${titleId}`}
      >
        <div className="modal__inner" onClick={depropigate} style={inner}>
          <header className="modal__header">
            {title ? (
              <H2 as={HEADING_TYPES.H5} id={`modal-${titleId}`}>{title}</H2>
            ) : null}
          </header>
          { hideOverflow
            ? null
            : (<div role="presentation" className="modal__overflow-top" />)
          }
          <div className="modal__body" ref={this.setModalBodyRef} style={body}>
            <div className="modal__body-inner" ref={this.setModalBodyInnerRef}>
              {this.renderChildren()}
            </div>
          </div>
          { hideOverflow
            ? null
            : (<div role="presentation" className="modal__overflow-bottom" />)
          }
          {hideDismiss ? null : (
            <Button
              type={TYPES.LINK}
              onClick={onDismiss}
              ref={this.setCancelRef}
              onBlur={this.focusInitial}
              className="modal__close"
            >
              <span>{staticText.get('dismiss')}</span>
            </Button>
          )}
        </div>
      </section>
    )
  }
}

Modal.defaultProps = {
  type: MODAL_TYPES.DEFAULT,
}

Modal.propTypes = {
  onReturnFocus: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  hideDismiss: PropTypes.bool,
  hideOverflow: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.oneOf([
    MODAL_TYPES.DEFAULT,
    MODAL_TYPES.MOBILE_BOTTOM_ALIGNED,
    MODAL_TYPES.ROYAL,
    MODAL_TYPES.ROYAL_VIDEO,
  ]),
}

export default connect(
  (state) => ({
    staticText: state.staticText.getIn(['data', 'modal', 'data'], Map()),
  }),
)(Modal)
